import React, { useEffect, useState } from 'react';
import { compact, get } from 'lodash';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { sendHistory } from './_api';
import { ISendHistory, IStudyDetailSendHistory } from './_types';
import { useAppGlobals } from 'utils/hooks/useAppGlobals';
import { stateIsSame } from 'utils/componentOptimizatons';
import { useMuiGrid } from 'utils/hooks/useMuiGrid';
import { joinParams } from 'utils/study';
import { MuiGrid } from 'components/MuiGrid/MuiGrid';

const getRowId = (row: any) => row.timestamp;
const muiGridKey = 'sendHistoriesMUI';

const PlainStudyDetailSendHistory: React.FC<IStudyDetailSendHistory> = ({ study }) => {
  const { t } = useTranslation('StudyDetail');
  const { toggleLoader } = useAppGlobals();
  const [sendHistories, setSendHistories] = useState<ISendHistory[]>([]);

  const { injectColumnWidthsIntoColumns, reorderColumnsByGridSettings } = useMuiGrid(muiGridKey);

  const loadEntity = async () => {
    toggleLoader();
    const productId = get(study, 'archive.id');
    const studyUid = get(study, 'studyInstanceUid');
    const sendHistories = await sendHistory(productId, studyUid);
    if (sendHistories) {
      setSendHistories(sendHistories);
    }
    toggleLoader(false);
  };

  useEffect(() => {
    loadEntity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = reorderColumnsByGridSettings(
    injectColumnWidthsIntoColumns(
      compact([
        {
          field: 'timestamp',
          headerName: t('timestamp'),
          width: 135,
          type: 'date',
          valueGetter: (value: any) => value && new Date(value),
          valueFormatter: (value: any) => value && format(value, 'dd. MM. yyyy HH:mm:ss'),
        },
        {
          field: 'username',
          headerName: t('username'),
          valueGetter: (value: any, row: any) => get(row, 'user.username'),
          width: 160,
        },
        {
          field: 'fullname',
          headerName: t('fullname'),
          valueGetter: (value: any, row: any) =>
            `${joinParams([get(row, 'user.lastName'), get(row, 'user.firstName')])}`,
          width: 180,
        },
        { field: 'network', headerName: t('network'), width: 90 },
        { field: 'destName', headerName: t('destName'), width: 250, flex: 1 },
      ]),
    ),
  );

  return (
    <MuiGrid
      gridKey={muiGridKey}
      rows={sendHistories}
      columns={columns}
      getRowId={getRowId}
      initialSortMode={[{ field: 'timestamp', sort: 'desc' }]}
    />
  );
};

export const StudyDetailSendHistory = React.memo(PlainStudyDetailSendHistory, stateIsSame);
